import { PROVIDER_LABELS, PROVIDER_ICONS } from '@/components/ai/aiProviderConstants';
import { t } from '@/lib/i18n';
import type { RorosDelegationConfig } from '@/services/roros/ai-operators.service';


export type TriggerImpact = 'root' | 'trunk' | 'branch' | 'leaf';

export type ActorEditorAiConfig = {
  provider: string;
  enabled: boolean;
};

export type ActorEditorProviderOption = {
  id: string;
  title: string;
  description: string;
  icon: string;
};

export type FormSnapshot = {
  // Shared (all types)
  actorType: string;
  selectedUserId: string | null;
  name: string;
  expertiseScope: string;
  adoptionLevel: string;
  selectedDomainIds: string[];
  imageAttachmentId: string | null;
  // AI operator fields (from AiOperatorEditor)
  modelTier: 'default' | 'complex';
  instructions: string;
  capabilities: string[];
  canDelegate: boolean;
  maxParallel: number | '';
  // budgetCapUsd removed — AC-11 superseded; budget owned by Role Contract
  provider: string | null;
  // Governance (Wave D: no moderator toggles — org-defaults managed separately)
  // AC-AGS-M4-1: routing fields
  routingDescription: string;
  isCoordinator: boolean;
};

export type DomainStanceEntry = { domainId: number; stance: string };

export type PersonaLocalState = {
  voiceDescription: string;
  styleDescription: string;
  valuesPriorities: string;
  decisionStyle: string;
  primaryLanguage: string;
  languageRules: string;
  domainStances: DomainStanceEntry[];
  provider: string | null;
  modelTier: string | null;
  instructions: string | null;
};

export const PERSONA_DEFAULTS: PersonaLocalState = {
  voiceDescription: '',
  styleDescription: '',
  valuesPriorities: '',
  decisionStyle: '',
  primaryLanguage: 'en',
  languageRules: '',
  domainStances: [],
  provider: null,
  modelTier: null,
  instructions: null,
};

export function arrayUnordered<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  for (const v of b) if (!setA.has(v)) return false;
  return true;
}

export function diffSnapshots(
  base: FormSnapshot,
  cur: FormSnapshot,
): Partial<Record<keyof FormSnapshot, boolean>> {
  const domainsDiff = (() => {
    if (base.selectedDomainIds.length !== cur.selectedDomainIds.length) return true;
    const setA = new Set(base.selectedDomainIds);
    for (const id of cur.selectedDomainIds) {
      if (!setA.has(id)) return true;
    }
    return false;
  })();
  return {
    actorType: base.actorType !== cur.actorType,
    selectedUserId: base.selectedUserId !== cur.selectedUserId,
    name: base.name !== cur.name,
    expertiseScope: base.expertiseScope !== cur.expertiseScope,
    adoptionLevel: base.adoptionLevel !== cur.adoptionLevel,
    selectedDomainIds: domainsDiff,
    imageAttachmentId: base.imageAttachmentId !== cur.imageAttachmentId,
    modelTier: base.modelTier !== cur.modelTier,
    instructions: base.instructions !== cur.instructions,
    capabilities: !arrayUnordered(base.capabilities, cur.capabilities),
    canDelegate: base.canDelegate !== cur.canDelegate,
    maxParallel: base.maxParallel !== cur.maxParallel,
    provider: base.provider !== cur.provider,
    routingDescription: base.routingDescription !== cur.routingDescription,
    isCoordinator: base.isCoordinator !== cur.isCoordinator,
  };
}

// Fields per tab for dirty-dot tracking
export const IDENTITY_FIELDS: Array<keyof FormSnapshot> = [
  'actorType', 'selectedUserId', 'name', 'expertiseScope', 'adoptionLevel',
  'selectedDomainIds', 'imageAttachmentId',
];
export const OPERATOR_FIELDS: Array<keyof FormSnapshot> = [
  'modelTier', 'instructions', 'capabilities', 'canDelegate', 'maxParallel', 'provider',
  'routingDescription', 'isCoordinator',
];
export const KP_OPERATOR_FIELDS: Array<keyof FormSnapshot> = ['provider'];

export const TRIGGER_IMPACT_OPTIONS: Array<{ value: TriggerImpact }> = [
  { value: 'root' },
  { value: 'trunk' },
  { value: 'branch' },
  { value: 'leaf' },
];



export function buildDelegationConfig(
  canDelegate: boolean,
  maxParallel: number | '',
  existing: RorosDelegationConfig | null | undefined,
  decisionTriggers: TriggerImpact[],
): RorosDelegationConfig {
  const config: RorosDelegationConfig = { can_delegate: canDelegate };
  if (typeof maxParallel === 'number') config.max_parallel = maxParallel;

  config.is_decision_moderator = !!existing?.is_decision_moderator;
  config.is_meeting_moderator = !!existing?.is_meeting_moderator;

  if (existing?.is_decision_moderator) {
    config.moderator_trigger_rules = {
      auto_invoke_on: decisionTriggers.length > 0 ? decisionTriggers : ['root', 'trunk'],
    };
  }

  return config;
}

export function buildProviderOptions(aiConfigs: ActorEditorAiConfig[]): ActorEditorProviderOption[] {
  return aiConfigs
    .filter((config) => config.enabled)
    .map((config) => ({
      id: config.provider,
      title: PROVIDER_LABELS[config.provider] ?? config.provider,
      description: t('Configured provider'),
      icon: PROVIDER_ICONS[config.provider] ?? '🔮',
    }));
}

export function adoptionLevelLabel(level: string | null | undefined): string {
  switch (level) {
    case 'shadow': return t('Shadow');
    case 'copilot': return t('Copilot');
    case 'autopilot': return t('Autopilot');
    default: return level ?? '';
  }
}

export function readinessResultLabel(result: string | null | undefined): string {
  switch (result) {
    case 'not_ready': return t('Not Ready');
    case 'shadow_ready': return t('Shadow Ready');
    case 'copilot_ready': return t('Copilot Ready');
    case 'autopilot_ready': return t('Autopilot Ready');
    default: return result ?? '';
  }
}
